import { Router } from "express";
import { and, count, countDistinct, desc, eq, gte, inArray, lt, lte, ne, or, sql, type SQL } from "drizzle-orm";
import { db } from "../../db/index";
import { users } from "../../db/schema/identity";
import { employeeDetails } from "../../db/schema/employee";
import { roles, roleUser } from "../../db/schema/rbac";
import { leaves, leaveTypes, employeeLeaveQuotas } from "../../db/schema/leave";
import { attendances } from "../../db/schema/attendance";
import { projects, tasks, taskboardColumns, taskUsers, projectTimeLogs } from "../../db/schema/projects";
import { invoices, expenses } from "../../db/schema/finance";
import { leads, leadStatus } from "../../db/schema/crm";
import { holidays } from "../../db/schema/hr";
import { notices } from "../../db/schema/core";
import { requireAuth, getEffectivePermissions, SCOPE } from "../../rbac/index";

/**
 * Dashboard API — role-aware real data, ported from legacy V2 DashboardController
 * (index L49–685, kpis L687–726). Variant resolution mirrors legacy:
 *   admin role OR view_attendance=ALL (seesAll) → admin variant;  client role → minimal;  else employee.
 * Every query is company-scoped. Widgets over not-yet-seeded modules (projects/invoices/leads/tickets/
 * timelogs) return real zeros/empty — the screen shows honest empty states, never fabricated numbers.
 */
export const dashboardRouter = Router();
dashboardRouter.use(requireAuth);

function weekRangeUtc(): { start: Date; end: Date } {
  const now = new Date();
  const day = (now.getUTCDay() + 6) % 7; // Mon=0
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - day, 0, 0, 0));
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 7);
  return { start, end };
}
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function mmdd(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const minutesSum = sql<number>`COALESCE(SUM(CAST(${projectTimeLogs.totalMinutes} AS UNSIGNED)), 0)`;

dashboardRouter.get("/dashboard", async (req, res) => {
  const companyId = req.auth!.companyId;
  const userId = req.auth!.userId;

  const [me] = await db
    .select({ name: users.name, image: users.image })
    .from(users)
    .where(eq(users.id, userId))
    .limit(1);
  const roleNames = (
    await db
      .select({ name: roles.name })
      .from(roleUser)
      .innerJoin(roles, eq(roles.id, roleUser.roleId))
      .where(eq(roleUser.userId, userId))
  ).map((r) => r.name);
  const perms = await getEffectivePermissions(userId);
  const seesAll = perms["view_attendance"] === SCOPE.ALL;
  const isAdmin = roleNames.includes("admin");
  const isClient = roleNames.includes("client") && !isAdmin && !roleNames.includes("employee");
  let variant: "admin" | "employee" | "client" = isAdmin || seesAll ? "admin" : isClient ? "client" : "employee";

  // Admin-only "Viewing as" preview (legacy ChromeController@viewAs): ?as=<role> swaps which
  // dashboard variant renders. Data stays the requester's own — it's a layout preview.
  const as = typeof req.query.as === "string" ? req.query.as.toLowerCase() : undefined;
  let previewing: string | null = null;
  if (as && isAdmin) {
    if (as === "employee") variant = "employee";
    else if (as === "client") variant = "client";
    else variant = "admin"; // admin / manager / hr / qa → company-wide layout
    if (as !== "admin") previewing = as;
  }

  const today = new Date();
  const todayStr = isoDate(today);
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const week = weekRangeUtc();
  const d7ago = new Date(today.getTime() - 7 * 86400000);

  // active employee headcount (users w/ employee_details, not deactivated) — company-scoped
  const staffRows = await db
    .select({ id: users.id })
    .from(users)
    .innerJoin(employeeDetails, eq(employeeDetails.userId, users.id))
    .where(and(eq(users.companyId, companyId), ne(users.status, "deactive")));
  const staffIds = staffRows.map((r) => r.id);

  // ── Punch card (legacy L101–121) ──
  const [openPunch] = await db
    .select({ id: attendances.id, clockIn: attendances.clockInTime })
    .from(attendances)
    .where(and(eq(attendances.userId, userId), sql`${attendances.clockOutTime} is null`))
    .orderBy(desc(attendances.clockInTime))
    .limit(1);
  const [lastToday] = await db
    .select({ clockIn: attendances.clockInTime, clockOut: attendances.clockOutTime })
    .from(attendances)
    .where(and(eq(attendances.userId, userId), sql`DATE(${attendances.clockInTime}) = ${todayStr}`))
    .orderBy(desc(attendances.clockInTime))
    .limit(1);
  const punch = {
    isOpen: !!openPunch,
    openId: openPunch?.id ?? null,
    lastIn: openPunch?.clockIn ?? lastToday?.clockIn ?? null,
    lastOut: lastToday?.clockOut ?? null,
  };

  // ── helpers ──
  const c1 = (table: any, where: SQL | undefined) =>
    db.select({ c: count() }).from(table).where(where).then((r) => Number(r[0]?.c ?? 0));

  // ── Today attendance summary + donut (legacy L205–215, 357–388) ──
  let present = 0;
  let onLeave = 0;
  if (staffIds.length) {
    const [p] = await db
      .select({ c: countDistinct(attendances.userId) })
      .from(attendances)
      .where(and(inArray(attendances.userId, staffIds), sql`DATE(${attendances.clockInTime}) = ${todayStr}`));
    present = Number(p?.c ?? 0);
    const [l] = await db
      .select({ c: countDistinct(leaves.userId) })
      .from(leaves)
      .where(and(inArray(leaves.userId, staffIds), eq(leaves.status, "approved"), sql`${leaves.leaveDate} = ${todayStr}`));
    onLeave = Number(l?.c ?? 0);
  }
  const headcount = staffIds.length;
  const todayAttendance = {
    headcount,
    present,
    onLeave,
    absent: Math.max(0, headcount - present - onLeave),
    notPunched: Math.max(0, headcount - present - onLeave),
  };

  // ── Birthdays today + celebrations (30d) (legacy L176–181, 450–488) ──
  const empPeople = await db
    .select({
      id: users.id,
      name: users.name,
      image: users.image,
      dob: employeeDetails.dateOfBirth,
      joining: employeeDetails.joiningDate,
    })
    .from(employeeDetails)
    .innerJoin(users, eq(users.id, employeeDetails.userId))
    .where(and(eq(users.companyId, companyId), eq(users.status, "active")));
  const todayMd = mmdd(today);
  const window30: Record<string, number> = {};
  for (let i = 0; i <= 30; i++) {
    const d = new Date(today.getTime() + i * 86400000);
    if (!(mmdd(d) in window30)) window30[mmdd(d)] = i;
  }
  const birthdaysToday: { id: number; name: string; image: string | null }[] = [];
  const celebrations: { id: number; name: string; image: string | null; kind: "birthday" | "anniversary"; inDays: number; years?: number }[] = [];
  for (const p of empPeople) {
    if (p.dob) {
      const md = mmdd(new Date(p.dob as unknown as string));
      if (md === todayMd) birthdaysToday.push({ id: p.id, name: p.name, image: p.image });
      if (md in window30) celebrations.push({ id: p.id, name: p.name, image: p.image, kind: "birthday", inDays: window30[md]! });
    }
    if (p.joining) {
      const jd = new Date(p.joining as unknown as string);
      const md = mmdd(jd);
      if (md in window30) {
        const years = today.getUTCFullYear() - jd.getUTCFullYear();
        if (years >= 1) celebrations.push({ id: p.id, name: p.name, image: p.image, kind: "anniversary", inDays: window30[md]!, years });
      }
    }
  }
  celebrations.sort((a, b) => a.inDays - b.inDays);

  // ── Common: upcoming holiday + announcements (legacy L127–141) ──
  const [upcomingHoliday] = await db
    .select({ id: holidays.id, date: holidays.date, occasion: holidays.occassion })
    .from(holidays)
    .where(and(eq(holidays.companyId, companyId), sql`${holidays.date} >= ${todayStr}`))
    .orderBy(holidays.date)
    .limit(1);
  const announcements = await db
    .select({ id: notices.id, heading: notices.heading, createdAt: notices.createdAt })
    .from(notices)
    .where(eq(notices.companyId, companyId))
    .orderBy(desc(notices.createdAt))
    .limit(4);

  // ── KPIs (legacy kpis() L687–726) ──
  const hoursThisWeek = async (uid?: number) => {
    const where = [gte(projectTimeLogs.startTime, week.start), lt(projectTimeLogs.startTime, week.end)];
    if (uid) where.push(eq(projectTimeLogs.userId, uid));
    const [r] = await db.select({ m: minutesSum }).from(projectTimeLogs).where(and(...where));
    return Math.round(Number(r?.m ?? 0) / 60);
  };

  let kpis: { label: string; value: number; unit?: string; tone: string; href?: string }[] = [];
  if (variant === "admin") {
    const [proj, openInv, leads7, pendLeave, pendExp] = await Promise.all([
      c1(projects, and(eq(projects.companyId, companyId), inArray(projects.status, ["in progress", "not started"]))),
      c1(invoices, and(eq(invoices.companyId, companyId), inArray(invoices.status, ["unpaid", "partial"]))),
      c1(leads, and(eq(leads.companyId, companyId), gte(leads.createdAt, d7ago))),
      c1(leaves, and(eq(leaves.companyId, companyId), eq(leaves.status, "pending"))),
      c1(expenses, and(eq(expenses.companyId, companyId), eq(expenses.status, "pending"))),
    ]);
    kpis = [
      { label: "Projects in flight", value: proj, tone: "blue", href: "/projects" },
      { label: "Hours this week", value: await hoursThisWeek(), unit: "h", tone: "amber", href: "/timelogs" },
      { label: "Pending approvals", value: pendLeave + pendExp, tone: "blue", href: "/leaves" },
      { label: "Open invoices", value: openInv, tone: "red", href: "/invoices" },
      { label: "New leads (7d)", value: leads7, tone: "green", href: "/leads" },
    ];
  } else if (variant === "employee") {
    const [openTasks, myLeavesYear, pendClaims] = await Promise.all([
      db
        .select({ c: count() })
        .from(tasks)
        .innerJoin(taskUsers, eq(taskUsers.taskId, tasks.id))
        .leftJoin(taskboardColumns, eq(taskboardColumns.id, tasks.boardColumnId))
        .where(and(eq(taskUsers.userId, userId), or(sql`${taskboardColumns.slug} is null`, ne(taskboardColumns.slug, "completed"))))
        .then((r) => Number(r[0]?.c ?? 0)),
      c1(leaves, and(eq(leaves.userId, userId), eq(leaves.status, "approved"), sql`YEAR(${leaves.leaveDate}) = ${today.getUTCFullYear()}`)),
      c1(expenses, and(eq(expenses.userId, userId), eq(expenses.status, "pending"))),
    ]);
    kpis = [
      { label: "My open tasks", value: openTasks, tone: "blue", href: "/tasks" },
      { label: "Hours this week", value: await hoursThisWeek(userId), unit: "h", tone: "amber", href: "/timelogs" },
      { label: "My leaves (this year)", value: myLeavesYear, tone: "green", href: "/leaves" },
      { label: "Pending claims", value: pendClaims, tone: "red", href: "/reimbursements" },
    ];
  }

  const base: Record<string, unknown> = {
    meta: { variant, seesAll, name: me?.name ?? "", role: roleNames[0] ?? "employee", previewing },
    punch,
    kpis,
    todayAttendance,
    birthdaysToday,
    celebrations: celebrations.slice(0, 9),
    upcomingHoliday: upcomingHoliday ?? null,
    announcements,
  };

  // ── Admin block ──
  if (variant === "admin") {
    const [pendLeave, pendExp] = await Promise.all([
      c1(leaves, and(eq(leaves.companyId, companyId), eq(leaves.status, "pending"))),
      c1(expenses, and(eq(expenses.companyId, companyId), eq(expenses.status, "pending"))),
    ]);
    const pendingLeaveRows = await db
      .select({
        id: leaves.id,
        userId: leaves.userId,
        userName: users.name,
        type: leaveTypes.typeName,
        leaveDate: leaves.leaveDate,
        duration: leaves.duration,
        reason: leaves.reason,
      })
      .from(leaves)
      .innerJoin(users, eq(users.id, leaves.userId))
      .leftJoin(leaveTypes, eq(leaveTypes.id, leaves.leaveTypeId))
      .where(and(eq(leaves.companyId, companyId), eq(leaves.status, "pending")))
      .orderBy(desc(leaves.id))
      .limit(3);

    const activeProjectsRaw = await db
      .select({ id: projects.id, name: projects.projectName, status: projects.status, deadline: projects.deadline })
      .from(projects)
      .where(and(eq(projects.companyId, companyId), inArray(projects.status, ["in progress", "not started"])))
      .orderBy(desc(projects.updatedAt))
      .limit(5);
    const activeProjects = await Promise.all(
      activeProjectsRaw.map(async (p) => {
        const total = await c1(tasks, eq(tasks.projectId, p.id));
        const done = await db
          .select({ c: count() })
          .from(tasks)
          .leftJoin(taskboardColumns, eq(taskboardColumns.id, tasks.boardColumnId))
          .where(and(eq(tasks.projectId, p.id), eq(taskboardColumns.slug, "completed")))
          .then((r) => Number(r[0]?.c ?? 0));
        return { ...p, taskTotal: total, taskDone: done, progressPct: total > 0 ? Math.round((done / total) * 100) : 0 };
      }),
    );

    const [overdueTasks, overdueInvoices, overdueProjects] = await Promise.all([
      db
        .select({ c: count() })
        .from(tasks)
        .leftJoin(taskboardColumns, eq(taskboardColumns.id, tasks.boardColumnId))
        .where(and(sql`DATE(${tasks.dueDate}) < ${todayStr}`, or(sql`${taskboardColumns.slug} is null`, ne(taskboardColumns.slug, "completed"))))
        .then((r) => Number(r[0]?.c ?? 0)),
      c1(invoices, and(eq(invoices.companyId, companyId), inArray(invoices.status, ["unpaid", "partial"]), sql`${invoices.dueDate} < ${todayStr}`)),
      c1(projects, and(eq(projects.companyId, companyId), sql`${projects.status} not in ('finished','canceled')`, sql`${projects.deadline} < ${todayStr}`)),
    ]);

    const monthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0, 23, 59, 59));
    const lastMonthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
    const lastMonthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0, 23, 59, 59));
    const sumInvoice = (where: SQL) =>
      db.select({ s: sql<number>`COALESCE(SUM(${invoices.total}),0)` }).from(invoices).where(where).then((r) => Number(r[0]?.s ?? 0));
    const [paidThis, paidLast, outstanding] = await Promise.all([
      sumInvoice(and(eq(invoices.companyId, companyId), eq(invoices.status, "paid"), gte(invoices.updatedAt, monthStart), lte(invoices.updatedAt, monthEnd))!),
      sumInvoice(and(eq(invoices.companyId, companyId), eq(invoices.status, "paid"), gte(invoices.updatedAt, lastMonthStart), lte(invoices.updatedAt, lastMonthEnd))!),
      db.select({ s: sql<number>`COALESCE(SUM(${invoices.dueAmount}),0)` }).from(invoices).where(and(eq(invoices.companyId, companyId), inArray(invoices.status, ["unpaid", "partial"]))).then((r) => Number(r[0]?.s ?? 0)),
    ]);

    const leadFunnel = await db
      .select({
        id: leadStatus.id,
        type: leadStatus.type,
        color: leadStatus.labelColor,
        priority: leadStatus.priority,
        leadCount: sql<number>`(SELECT COUNT(*) FROM leads WHERE leads.status_id = ${leadStatus.id})`,
      })
      .from(leadStatus)
      .where(eq(leadStatus.companyId, companyId))
      .orderBy(leadStatus.priority);

    const hoursLeaderboard = await db
      .select({ userId: projectTimeLogs.userId, name: users.name, image: users.image, minutes: minutesSum })
      .from(projectTimeLogs)
      .innerJoin(users, eq(users.id, projectTimeLogs.userId))
      .where(and(gte(projectTimeLogs.startTime, week.start), lt(projectTimeLogs.startTime, week.end)))
      .groupBy(projectTimeLogs.userId, users.name, users.image)
      .orderBy(desc(minutesSum))
      .limit(5);

    Object.assign(base, {
      pendingApprovals: { leaves: pendLeave, expenses: pendExp, wfh: 0, corrections: 0 },
      pendingLeaveRows,
      activeProjects,
      overdue: { tasks: overdueTasks, invoices: overdueInvoices, projects: overdueProjects },
      revenue: { paid: paidThis, outstanding, changePct: paidLast > 0 ? Math.round(((paidThis - paidLast) / paidLast) * 100) : null },
      leadFunnel,
      hoursLeaderboard: hoursLeaderboard.map((h) => ({ ...h, minutes: Number(h.minutes) })),
    });
  }

  // ── Employee block (legacy L491–636) ──
  if (variant === "employee") {
    const myTimeLogs = await db
      .select({ id: projectTimeLogs.id, startTime: projectTimeLogs.startTime, minutes: projectTimeLogs.totalMinutes, projectName: projects.projectName, taskHeading: tasks.heading })
      .from(projectTimeLogs)
      .leftJoin(projects, eq(projects.id, projectTimeLogs.projectId))
      .leftJoin(tasks, eq(tasks.id, projectTimeLogs.taskId))
      .where(eq(projectTimeLogs.userId, userId))
      .orderBy(desc(projectTimeLogs.startTime))
      .limit(5);

    // current-month attendance roll-up (distinct days)
    const monthAtt = await db
      .select({ clockIn: attendances.clockInTime, late: attendances.late, halfDay: attendances.halfDay })
      .from(attendances)
      .where(and(eq(attendances.userId, userId), gte(attendances.clockInTime, monthStart)));
    const seen = new Set<string>();
    const myAttendanceMonth = { present: 0, late: 0, halfDay: 0, absent: 0 };
    for (const a of monthAtt) {
      const d = isoDate(new Date(a.clockIn as unknown as string));
      if (seen.has(d)) continue;
      seen.add(d);
      myAttendanceMonth.present++;
      if (a.late === "yes") myAttendanceMonth.late++;
      if (a.halfDay === "yes") myAttendanceMonth.halfDay++;
    }
    let workingDays = 0;
    for (let c = new Date(monthStart); c <= today; c.setUTCDate(c.getUTCDate() + 1)) {
      const wd = c.getUTCDay();
      if (wd !== 0 && wd !== 6) workingDays++;
    }
    myAttendanceMonth.absent = Math.max(0, Math.max(1, workingDays) - myAttendanceMonth.present);

    const myTasks = await db
      .select({ id: tasks.id, heading: tasks.heading, dueDate: tasks.dueDate, projectName: projects.projectName, column: taskboardColumns.columnName })
      .from(tasks)
      .innerJoin(taskUsers, eq(taskUsers.taskId, tasks.id))
      .leftJoin(taskboardColumns, eq(taskboardColumns.id, tasks.boardColumnId))
      .leftJoin(projects, eq(projects.id, tasks.projectId))
      .where(and(eq(taskUsers.userId, userId), or(sql`${taskboardColumns.slug} is null`, ne(taskboardColumns.slug, "completed"))))
      .orderBy(tasks.dueDate)
      .limit(6);

    const myPendingLeaves = await c1(leaves, and(eq(leaves.userId, userId), eq(leaves.status, "pending")));

    // leave balances per type (quota − approved-this-year, half=0.5)
    const quotas = await db
      .select({ leaveTypeId: employeeLeaveQuotas.leaveTypeId, allocated: employeeLeaveQuotas.noOfLeaves, type: leaveTypes.typeName, color: leaveTypes.color })
      .from(employeeLeaveQuotas)
      .leftJoin(leaveTypes, eq(leaveTypes.id, employeeLeaveQuotas.leaveTypeId))
      .where(eq(employeeLeaveQuotas.userId, userId));
    const myLeaveBalances = await Promise.all(
      quotas.map(async (q) => {
        const [u] = await db
          .select({ used: sql<number>`COALESCE(SUM(CASE WHEN ${leaves.duration} = 'half day' THEN 0.5 ELSE 1 END),0)` })
          .from(leaves)
          .where(and(eq(leaves.userId, userId), eq(leaves.leaveTypeId, q.leaveTypeId), eq(leaves.status, "approved"), sql`YEAR(${leaves.leaveDate}) = ${today.getUTCFullYear()}`));
        const used = Number(u?.used ?? 0);
        const allocated = Number(q.allocated ?? 0);
        return { type: q.type ?? "Leave", color: q.color ?? "#3b82f6", allocated, used, left: Math.max(0, allocated - used) };
      }),
    );

    // 7-day hours
    const weekStart7 = new Date(today.getTime() - 6 * 86400000);
    weekStart7.setUTCHours(0, 0, 0, 0);
    const weekRows = await db
      .select({ startTime: projectTimeLogs.startTime, minutes: projectTimeLogs.totalMinutes })
      .from(projectTimeLogs)
      .where(and(eq(projectTimeLogs.userId, userId), gte(projectTimeLogs.startTime, weekStart7)));
    const myWeekDailyMinutes: { day: string; minutes: number }[] = [];
    for (let i = 6; i >= 0; i--) myWeekDailyMinutes.push({ day: isoDate(new Date(today.getTime() - i * 86400000)), minutes: 0 });
    const idx = new Map(myWeekDailyMinutes.map((d, i) => [d.day, i]));
    for (const r of weekRows) {
      const d = isoDate(new Date(r.startTime as unknown as string));
      const i = idx.get(d);
      if (i !== undefined) myWeekDailyMinutes[i]!.minutes += Number(r.minutes ?? 0);
    }

    Object.assign(base, {
      myTimeLogs: myTimeLogs.map((t) => ({ ...t, minutes: Number(t.minutes ?? 0) })),
      myAttendanceMonth,
      myTasks,
      myPending: { leaves: myPendingLeaves, wfh: 0, corrections: 0 },
      myLeaveBalances,
      myWeekDailyMinutes,
    });
  }

  res.json(base);
});
